import { EnvironmentEnum } from "../utils";
import type { CommonSettingsType } from "../settings/fetchCommonSettings";
import type { FetchFnReturnType } from "./backendFetch";
import type { FormRequestResponseType } from "../server/v2/endpoints/FormEndpoint";
import type { RequestExceptionReturnType } from "../types/CommonTypes";

// This type should be used whenever we send GDPR consents to DXP Forms gateway.
// Consent is a string because of legacy reasons. Booleans are not always parsed properly at eHub side...
export type GdprConsentType = { link: string; consent: "true" | "false" }[];

/**
 * Retrieve the link to the eHub DXP endpoint based on the current environment variables.
 */
export const getDxpEhubEndpointUrl = (dxpSettingsServiceUrl: string, environment: EnvironmentEnum): string => {
    return `${dxpSettingsServiceUrl}${
        // DXP + eHub quirks: DXP UAT connects to eHub ACC by default, however eHub ACC isnt good enough to properly test.
        // The DXP UAT eHub preview does connect to eHub preview, so add this to the default DXP endpoint URL used on acceptance.
        environment === EnvironmentEnum.Acceptance ? "/preview" : ""
    }/dxp/forms/ehub/upload`;
};

export type FormInfoResponseType = { success: boolean; detailedError?: string };
/**
 * Send information to our local Form endpoint which in turn sends the information to eHub or the country specific endpoint.
 * See https://cde.toyota-europe.com/confluence/display/OCDE/API+documentation
 *
 * Added 'country' as a required param as this seems to trigger some specific logic server side vs the country param in the endpoint
 */
export const sendFormInfo = async <T extends { country: string }>(
    payload: T,
    commonSettings: CommonSettingsType,
): Promise<FormInfoResponseType> => {
    const { resourcePath, language } = commonSettings;
    let { country } = commonSettings;

    // DXP Forms Gateway doesnt support the global 'zz' country.
    // Override this to TZ as this is also the country used as a reference in most global use cases.
    if (commonSettings.country === "zz") {
        payload.country = "tz";
        country = "tz";
    }

    const response = await fetch(`${resourcePath}/v1/api/form/${country}/${language}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ payload }),
    });

    const parsedResponse = (await response.json()) as
        | FetchFnReturnType<FormRequestResponseType>
        | RequestExceptionReturnType;

    if ("fetchResult" in parsedResponse) {
        // fetchResult indicates a successful response (but not necessarily a successful form submission)
        if (parsedResponse.errorLogs?.length) {
            return { success: false, detailedError: parsedResponse.errorLogs[0].message as string };
        }

        return { success: !!parsedResponse.fetchResult?.success };
    }

    return { success: false, detailedError: String(parsedResponse.stack) };
};
